import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ResponsiveContainer, BarChart, Bar, XAxis, Tooltip, Cell } from "recharts";
import { Users, Package, Euro, TrendingUp, ShieldAlert, Ban, CheckCircle2, Search, Trash2, Wallet, Activity } from "lucide-react";

const resolveImg = (img) => !img ? "https://images.unsplash.com/photo-1581147036324-c1c89c2c8b5c?w=200&q=80" : img.startsWith("http") ? img : fileUrl(img);

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div data-testid={`stat-${label.toLowerCase().replace(/[^a-z]/g, "-")}`} className="bg-white border border-black/10 rounded-2xl p-5">
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-3 ${accent}`}><Icon className="w-4 h-4" /></div>
      <p className="font-display text-2xl font-bold tracking-tight">{value}</p>
      <p className="text-sm text-muted-foreground">{label}</p>
    </div>
  );
}

export default function Admin() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [users, setUsers] = useState([]);
  const [listings, setListings] = useState([]);
  const [disputes, setDisputes] = useState([]);
  const [uSearch, setUSearch] = useState("");
  const [lSearch, setLSearch] = useState("");
  const [resolveTarget, setResolveTarget] = useState(null);

  useEffect(() => {
    if (!loading && (!user || !user.is_admin)) navigate("/");
  }, [user, loading, navigate]);

  const loadAll = () => {
    api.get("/admin/analytics").then((r) => setAnalytics(r.data)).catch(() => {});
    api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
    api.get("/admin/listings").then((r) => setListings(r.data)).catch(() => {});
    api.get("/admin/disputes").then((r) => setDisputes(r.data)).catch(() => {});
  };
  useEffect(() => { if (user?.is_admin) loadAll(); }, [user]);

  const searchUsers = (v) => { setUSearch(v); api.get("/admin/users", { params: v ? { search: v } : {} }).then((r) => setUsers(r.data)); };
  const searchListings = (v) => { setLSearch(v); api.get("/admin/listings", { params: v ? { search: v } : {} }).then((r) => setListings(r.data)); };

  const toggleSuspend = async (u) => {
    try {
      await api.post(`/admin/users/${u.user_id}/suspend`, { suspended: !u.suspended });
      toast.success(u.suspended ? "User reinstated" : "User suspended");
      searchUsers(uSearch);
    } catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
  };

  const removeListing = async (id) => {
    try { await api.delete(`/admin/listings/${id}`); toast.success("Listing removed"); searchListings(lSearch); loadAll(); }
    catch { toast.error("Failed to remove"); }
  };

  if (loading || !user?.is_admin) return <div className="py-32 flex justify-center"><div className="w-10 h-10 border-4 border-[#FF5A00] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
      <div className="flex items-center gap-2">
        <ShieldAlert className="w-6 h-6 text-[#FF5A00]" />
        <h1 className="font-display text-4xl font-bold tracking-tight">Admin</h1>
      </div>
      <p className="text-muted-foreground mt-1">Moderate the marketplace and monitor the business.</p>

      <Tabs defaultValue="analytics" className="mt-8">
        <TabsList className="bg-[#E3E8E1] rounded-full p-1 flex-wrap h-auto">
          <TabsTrigger data-testid="admin-tab-analytics" value="analytics" className="rounded-full data-[state=active]:bg-white">Analytics</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-users" value="users" className="rounded-full data-[state=active]:bg-white">Users ({users.length})</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-listings" value="listings" className="rounded-full data-[state=active]:bg-white">Listings ({listings.length})</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-disputes" value="disputes" className="rounded-full data-[state=active]:bg-white">Disputes ({disputes.filter((d) => d.status === "open").length})</TabsTrigger>
        </TabsList>

        {/* Analytics */}
        <TabsContent value="analytics" className="mt-6">
          {analytics && (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <Stat icon={Users} label="Total users" value={analytics.users_total} accent="bg-blue-100 text-blue-700" />
                <Stat icon={TrendingUp} label="New (7d)" value={analytics.users_new_7d} accent="bg-green-100 text-green-700" />
                <Stat icon={Package} label="Active listings" value={analytics.listings_active} accent="bg-amber-100 text-amber-700" />
                <Stat icon={Package} label="Sold" value={analytics.listings_sold} accent="bg-gray-100 text-gray-700" />
                <Stat icon={Euro} label="GMV" value={`€${analytics.gmv}`} accent="bg-[#FF5A00]/10 text-[#FF5A00]" />
                <Stat icon={Euro} label="Revenue (commission)" value={`€${analytics.revenue}`} accent="bg-green-100 text-green-700" />
                <Stat icon={Activity} label="Active rentals" value={analytics.active_rentals} accent="bg-blue-100 text-blue-700" />
                <Stat icon={Wallet} label="Pending payouts" value={`€${analytics.pending_payouts}`} accent="bg-amber-100 text-amber-700" />
              </div>

              <div className="grid lg:grid-cols-3 gap-4 mt-4">
                <div className="lg:col-span-2 bg-white border border-black/10 rounded-2xl p-5">
                  <p className="font-display font-bold tracking-tight mb-4">Transaction volume (14 days)</p>
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={analytics.timeseries}>
                      <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} fontSize={11} tickLine={false} axisLine={false} interval={1} />
                      <Tooltip cursor={{ fill: "rgba(0,0,0,0.04)" }} formatter={(v) => [`€${v}`, "Volume"]} />
                      <Bar dataKey="volume" radius={[6, 6, 0, 0]}>
                        {analytics.timeseries.map((_, i) => <Cell key={i} fill="#FF5A00" />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="bg-white border border-black/10 rounded-2xl p-5">
                  <p className="font-display font-bold tracking-tight mb-4">Top categories</p>
                  <div className="space-y-3">
                    {analytics.top_categories.map((c) => (
                      <div key={c.category}>
                        <div className="flex justify-between text-sm mb-1"><span>{c.category}</span><span className="font-semibold">{c.count}</span></div>
                        <div className="h-2 bg-black/5 rounded-full overflow-hidden">
                          <div className="h-full bg-[#0A0A0A] rounded-full" style={{ width: `${(c.count / (analytics.listings_total || 1)) * 100}%` }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </TabsContent>

        {/* Users */}
        <TabsContent value="users" className="mt-6">
          <div className="relative max-w-sm mb-4">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="admin-user-search" value={uSearch} onChange={(e) => searchUsers(e.target.value)} placeholder="Search name or email…" className="pl-9 bg-white border-black/10 rounded-xl" />
          </div>
          <div className="space-y-2">
            {users.map((u) => (
              <div key={u.user_id} data-testid={`admin-user-${u.user_id}`} className="flex items-center gap-3 bg-white border border-black/10 rounded-2xl p-3">
                <Avatar className="w-10 h-10"><AvatarImage src={u.picture} /><AvatarFallback className="bg-[#E3E8E1] text-sm">{u.name?.[0]}</AvatarFallback></Avatar>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm truncate">{u.name}
                    {u.is_admin && <span className="ml-2 text-[10px] font-bold uppercase bg-[#FF5A00] text-white px-2 py-0.5 rounded-full">Admin</span>}
                    {u.suspended && <span className="ml-2 text-[10px] font-bold uppercase bg-red-100 text-red-700 px-2 py-0.5 rounded-full">Suspended</span>}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">{u.email} · {u.listing_count} listings · ★ {u.rating} ({u.review_count})</p>
                </div>
                {!u.is_admin && (
                  <button data-testid={`suspend-${u.user_id}`} onClick={() => toggleSuspend(u)} className={`flex items-center gap-1.5 text-xs font-medium rounded-full px-3 py-1.5 transition-colors ${u.suspended ? "bg-green-100 text-green-700 hover:bg-green-200" : "border border-red-200 text-red-600 hover:bg-red-50"}`}>
                    {u.suspended ? <><CheckCircle2 className="w-3.5 h-3.5" /> Reinstate</> : <><Ban className="w-3.5 h-3.5" /> Suspend</>}
                  </button>
                )}
              </div>
            ))}
          </div>
        </TabsContent>

        {/* Listings */}
        <TabsContent value="listings" className="mt-6">
          <div className="relative max-w-sm mb-4">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="admin-listing-search" value={lSearch} onChange={(e) => searchListings(e.target.value)} placeholder="Search title or owner…" className="pl-9 bg-white border-black/10 rounded-xl" />
          </div>
          <div className="space-y-2">
            {listings.map((l) => (
              <div key={l.id} data-testid={`admin-listing-${l.id}`} className="flex items-center gap-3 bg-white border border-black/10 rounded-2xl p-3">
                <img src={resolveImg(l.images?.[0])} alt="" className="w-12 h-12 rounded-lg object-cover" />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm truncate">{l.title} {l.is_sold && <span className="text-[10px] font-bold uppercase bg-black text-white px-1.5 py-0.5 rounded">Sold</span>}</p>
                  <p className="text-xs text-muted-foreground truncate">{l.category} · {l.owner_name} · {l.listing_type}</p>
                </div>
                <button onClick={() => navigate(`/listing/${l.id}`)} className="text-xs font-medium border border-black/15 rounded-full px-3 py-1.5 hover:border-black transition-colors">View</button>
                <button data-testid={`remove-listing-${l.id}`} onClick={() => removeListing(l.id)} className="p-2 rounded-full text-red-600 hover:bg-red-50 transition-colors"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* Disputes */}
        <TabsContent value="disputes" className="mt-6 space-y-3">
          {disputes.length === 0 ? <p className="text-muted-foreground text-center py-12">No disputes or claims.</p> : disputes.map((d) => (
            <div key={d.id} data-testid={`admin-dispute-${d.id}`} className="bg-white border border-black/10 rounded-2xl p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${d.type === "non_return" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-800"}`}>{d.type === "non_return" ? "Non-return" : "Dispute"}</span>
                    <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${d.status === "open" ? "bg-blue-100 text-blue-700" : "bg-gray-100 text-gray-600"}`}>{d.status}</span>
                  </div>
                  <p className="font-semibold mt-1.5">{d.subject}</p>
                  <p className="text-sm text-muted-foreground mt-0.5">{d.listing_title} · filed by {d.filer_name} vs {d.against_name}{d.deposit_amount > 0 && ` · deposit €${d.deposit_amount}`}</p>
                  <p className="text-sm mt-2">{d.description}</p>
                  {d.resolution && <p className="text-xs text-[#2E7D32] mt-2 font-medium">Resolution: {d.resolution}</p>}
                </div>
                {d.status === "open" && (
                  <button data-testid={`resolve-${d.id}`} onClick={() => setResolveTarget(d)} className="shrink-0 text-xs font-medium bg-[#0A0A0A] text-white rounded-full px-4 py-2 hover:bg-[#FF5A00] transition-colors">Resolve</button>
                )}
              </div>
            </div>
          ))}
        </TabsContent>
      </Tabs>

      <ResolveDialog target={resolveTarget} onClose={() => setResolveTarget(null)} onDone={loadAll} />
    </div>
  );
}

function ResolveDialog({ target, onClose, onDone }) {
  const [action, setAction] = useState("refund_renter");
  const [note, setNote] = useState("");
  const [refund, setRefund] = useState("");
  const [award, setAward] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (target) { setAction(target.type === "non_return" ? "award_owner" : "refund_renter"); setNote(""); setRefund(""); setAward(""); } }, [target]);
  if (!target) return null;
  const deposit = target.deposit_amount || 0;

  const submit = async () => {
    setBusy(true);
    try {
      const body = { action, note };
      if (action === "refund_renter") body.refund_amount = refund ? parseFloat(refund) : deposit;
      if (action === "award_owner") body.award_amount = award ? parseFloat(award) : deposit;
      if (action === "partial") { body.refund_amount = parseFloat(refund || 0); body.award_amount = parseFloat(award || 0); }
      await api.post(`/admin/disputes/${target.id}/resolve`, body);
      toast.success("Dispute resolved");
      onClose(); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to resolve"); }
    finally { setBusy(false); }
  };

  const ACTIONS = [
    { k: "refund_renter", l: "Refund deposit → renter" },
    { k: "award_owner", l: "Award deposit → owner" },
    { k: "partial", l: "Split (partial)" },
    { k: "reject", l: "Reject / close (no money)" },
  ];

  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle className="font-display tracking-tight">Resolve claim</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground -mt-1">{target.listing_title} · deposit €{deposit}</p>
        <div className="space-y-2 mt-2">
          {ACTIONS.map((a) => (
            <button key={a.k} data-testid={`resolve-action-${a.k}`} onClick={() => setAction(a.k)} className={`w-full text-left px-4 py-2.5 rounded-xl border text-sm font-medium transition-colors ${action === a.k ? "bg-black text-white border-black" : "bg-white border-black/15 hover:border-black"}`}>{a.l}</button>
          ))}
        </div>
        {(action === "refund_renter" || action === "partial") && (
          <Input data-testid="refund-amount" type="number" value={refund} onChange={(e) => setRefund(e.target.value)} placeholder={`Refund to renter (default €${deposit})`} className="bg-white border-black/10 rounded-xl" />
        )}
        {(action === "award_owner" || action === "partial") && (
          <Input data-testid="award-amount" type="number" value={award} onChange={(e) => setAward(e.target.value)} placeholder={`Award to owner (default €${deposit})`} className="bg-white border-black/10 rounded-xl" />
        )}
        <Textarea data-testid="resolve-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Resolution note (visible to both parties)…" className="bg-white border-black/10 rounded-xl" />
        <button data-testid="confirm-resolve-btn" onClick={submit} disabled={busy} className="w-full bg-[#0A0A0A] text-white rounded-full py-3 font-medium hover:bg-[#FF5A00] transition-colors disabled:opacity-60">
          {busy ? "Processing…" : "Confirm resolution"}
        </button>
      </DialogContent>
    </Dialog>
  );
}
